import React, { FC, useMemo, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useSelector } from 'react-redux';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { BasketMandatoryFeesDetailsProps } from '@components/v2/BasketMandatoryFeesDetails/index';
import { IDealerFee } from '@components/DealerFees/DealerFeesTemplate';
import { CARTE_GRISE_FEE } from '../../../constants/main';
import Spinner from '@components/Spinner';
import { theme } from '../../../styles/theme';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import isEmpty from 'lodash/isEmpty';

const MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON = 150;

const BasketMandatoryFeesTemplate: FC<BasketMandatoryFeesDetailsProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();
    const { currentDeal } = useCarDetailsDuck();
    const { formatMoney } = useMoneyFormatter();

    const isLoading = useSelector((state) => !!UIDuck.getOwnState(state).loading);

    const [isOpen, setIsOpen] = useState(true);
    const [openedLabelIds, setOpenedLabelIds] = useState([]);

    const dealerFees = useMemo(
        () => (!isEmpty(currentDeal?.extraFields?.dealerFees) ? JSON.parse(currentDeal?.extraFields?.dealerFees) : []),
        [currentDeal?.extraFields?.dealerFees]
    );

    const filteredMandatoryDealerFees = dealerFees.filter(
        (dealerFee: IDealerFee) => dealerFee.mandatory && dealerFee.id !== CARTE_GRISE_FEE
    );

    const handleDescriptionClick = (id: string) => {
        if (openedLabelIds.find((openedId) => openedId === id)) {
            return setOpenedLabelIds(openedLabelIds.filter((openedId) => openedId !== id));
        }

        return setOpenedLabelIds([...openedLabelIds, id]);
    };

    const isOpenedLabelById = (id: string) => openedLabelIds.find((openedId) => id === openedId);
    const getMoney = (price: number = 0) => {
        return price === 0 ? t('configurator.colors.includedInPrice') : formatMoney(price, true);
    };

    return (
        <div id="mandatoryFees" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton className="titleWrapperButton" onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t('basket.mandatoryFees.title').toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="fees">
                    <div className="description">
                        <p>{t('basket.mandatoryFees.description')}</p>
                    </div>
                    <div className="items">
                        {isLoading ? (
                            <Spinner size={20} border={3} color={theme.colors.black} />
                        ) : (
                            filteredMandatoryDealerFees
                                .sort((a: { title: string }, b: { title: string }) =>
                                    t(a.title).toLowerCase() > t(b.title).toLowerCase()
                                        ? 1
                                        : t(b.title).toLowerCase() > t(a.title).toLowerCase()
                                        ? -1
                                        : 0
                                )
                                .map((dealerFee: IDealerFee, key: number) => (
                                    <div key={key} className="item">
                                        <div className="item_title">
                                            <span>{t(dealerFee.title)}</span>
                                            <span className="price">{getMoney(dealerFee?.priceInclTax)}</span>
                                        </div>
                                        <div
                                            className={`item_description ${
                                                dealerFee.label?.length > MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON
                                                    ? 'item_description_min'
                                                    : ''
                                            } ${isOpenedLabelById(dealerFee.id) ? 'item_description_full' : ''}`}
                                        >
                                            <p>{dealerFee.label}</p>
                                        </div>
                                        {dealerFee.label?.length > MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON && (
                                            <div
                                                onClick={() => handleDescriptionClick(dealerFee.id)}
                                                className="item_link"
                                            >
                                                <button>
                                                    {isOpenedLabelById(dealerFee?.id)
                                                        ? t('basket.mandatoryFees.detail.hide')
                                                        : t('basket.mandatoryFees.detail.hide')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))
                        )}
                    </div>
                </div>
            </Collapsible>
        </div>
    );
};

export default BasketMandatoryFeesTemplate;
